/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getDimensionAlignedToDevicePixels } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isPageBlank } from "selectors/pageSelectors";
import { getProvidedMargin } from "selectors/zoom";
import { transformPDFToView, transformViewToPDF } from "dc-preview-utils/src/Utils/PageUtils";
import { getIsInPlaceMode } from "selectors/viewModeSelectors";
import {
  getTotalPages,
  getUseProgressiveRendering,
  isSinglePageMode,
  isFullScreenOrFitOnePageMode,
  getProgressivelyRenderedPageStatus,
  isPageByPageViewMode,
  isContinuousViewMode,
  isTwoColumnViewMode,
  isCoverPageMode,
  isViewWithScrolling,
  isFullScreenMode,
  isFitOneFullPageMode,
  getDocumentBottomSpace,
  getBottomPanelHeight
} from "selectors/documentSelectors";
import {
  getViewPortHeight,
  getDocumentVisibleHeight,
  getViewPortHeightWithoutPagesViewScrollbar
} from "selectors/documentViewSelectors";
import { getTransformMatrixFromPageGeometry } from "dc-preview-utils/src/Utils/PageUtils";
import * as constants from "constants/constants";
import { loadStatus } from "constants/enums";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { getViewMode } from "selectors/viewModeSelectors";
import { docViewModes } from "constants/enums";

// ToDO: refactor, after gotolocation, get currpage which is being used in gotolocation.

export const getZoomLevelDimension = (origDimension, zoomLevel) =>
  Math.ceil(getDimensionAlignedToDevicePixels(origDimension * zoomLevel));

export const getAnnotationFlagsUtil = () => {
  const renderAllAnnotsFlag = 0xffffffff;
  return renderAllAnnotsFlag;
};

export const setFixedWidthMode = (fixedWidthMode, stores) => {
  const providedMargin = getProvidedMargin(stores);
  if (fixedWidthMode) {
    stores.zoom.setFixedWidthMargin(providedMargin.left, providedMargin.right);
  } else {
    stores.zoom.removeFixedWidthMode();
  }
};

export const setShowCommentsPanel = (visibility, stores) => {
  // not validating the visibility as Boolean here because the value is coming from comments dropin where it is already validated
  stores.zoom.setIsCommentsPanelVisible(visibility);
};

const getModifiedRect = (rect, shouldIncludeBoundary) => {
  let left, right, top, bottom;
  left = Math.floor(rect.left);
  right = Math.floor(rect.right);
  top = Math.floor(rect.top);
  bottom = Math.floor(rect.bottom);

  // increase the boundary by 1px; For chrome, we get x and y coordinate 1px less than the actual value hence increasing the rect size by 1px.
  if (!shouldIncludeBoundary) {
    // shrink the box by 1px
    left += 1;
    right ? (right -= 1) : null;
    top += 1;
    bottom ? (bottom -= 1) : null;
  } else {
    // expand the box by 1px
    left ? (left -= 1) : null;
    right += 1;
    top ? (top -= 1) : null;
    bottom += 1;
  }
  return {
    left,
    right,
    top,
    bottom
  };
};

export const isPointInsideRectangle = (x, y, rectsArray, shouldIncludeBoundary = true) => {
  if (rectsArray) {
    for (let i = 0; i < rectsArray.length; i++) {
      const rect = rectsArray[i];
      const { left, right, top, bottom } = getModifiedRect(rect, shouldIncludeBoundary);
      if (shouldIncludeBoundary) {
        if (x >= left && x <= right && y >= top && y <= bottom) return true;
      } else {
        if (x > left && x < right && y > top && y < bottom) return true;
      }
    }
  }
  return false;
};

// Returns true if there is a scrollbar present in given direction for given element
export const hasScrollbar = (el, direction) => {
  if (direction === "horizontal") {
    return el.scrollWidth > el.clientWidth;
  } else if (direction === "vertical") {
    return el.scrollHeight > el.clientHeight;
  }
  return false;
};

/* 
minPagePercentToBeInView - this value determines the mininum % of page to be inside view so that we consider that page to be in view.
currY - the amount of items scrolled in y direction.
getItemHeight - height of each item with margins.
*/
export const itemsInView = (
  stores,
  currY,
  getItemHeight,
  padding,
  shouldConsiderScrollbar = false,
  minPagePercentToBeInView = 0
) => {
  if (isSinglePageMode(stores) || isFullScreenOrFitOnePageMode(stores)) {
    return {
      start: getCurrentPageIndex(stores),
      end: getCurrentPageIndex(stores)
    };
  }
  let viewPortHeight = !shouldConsiderScrollbar
    ? getViewPortHeight(stores)
    : getViewPortHeightWithoutPagesViewScrollbar(stores);
  if (getIsInPlaceMode(stores)) {
    viewPortHeight = getDocumentVisibleHeight(stores);
  }

  if (currY < padding) {
    viewPortHeight -= padding - currY;
  }
  if (currY >= padding) {
    currY -= padding;
  } else {
    currY = padding;
  }
  let i = 0;
  const totalPages = getTotalPages(stores);
  let firstPageInView = 0;
  const firstPagePercentToBeInView = (100 - minPagePercentToBeInView) / 100;
  while (i < totalPages && currY >= 0) {
    if (currY >= firstPagePercentToBeInView * getItemHeight(stores, i)) {
      firstPageInView = i + 1;
    }
    currY -= getItemHeight(stores, i);
    i++;
  }
  let partOfTopPageInView = -currY;

  viewPortHeight -= partOfTopPageInView;
  let lastPageInView = firstPageInView;
  let lastPagePercentToBeInView = minPagePercentToBeInView / 100;
  while (i < totalPages && viewPortHeight > 0) {
    if (viewPortHeight >= lastPagePercentToBeInView * getItemHeight(stores, i)) {
      lastPageInView = i;
    }
    viewPortHeight -= getItemHeight(stores, i);
    i++;
  }
  return {
    start: firstPageInView,
    end: lastPageInView
  };
};

export const populatePagesGeometryData = data => {
  const pagesGeometry = [];
  data.pages.forEach(pageGeometry => {
    pagesGeometry.push({
      origHeight: parseFloat(pageGeometry.height),
      origWidth: parseFloat(pageGeometry.width),
      tMatrix: getTransformMatrixFromPageGeometry(pageGeometry),
      minZoom: parseFloat(pageGeometry.minZoom),
      maxZoom: parseFloat(pageGeometry.maxZoom),
      boxes: pageGeometry.boxes
    });
  });
  return pagesGeometry;
};

export const transformRectPDFToView = (l, r, t, b, pageIndex, stores, zoomLevel, options = {}) => {
  const leftTop = transformPDFToView(l, t, pageIndex, stores, zoomLevel, options);
  const rightBottom = transformPDFToView(r, b, pageIndex, stores, zoomLevel, options);
  const left = Math.floor(Math.min(leftTop.x, rightBottom.x));
  const top = Math.floor(Math.min(leftTop.y, rightBottom.y));
  const right = Math.ceil(Math.max(leftTop.x, rightBottom.x));
  const bottom = Math.ceil(Math.max(leftTop.y, rightBottom.y));
  return {
    left,
    right,
    top,
    bottom
  };
};

/**
 *
 * @param { Takes the left, right, top, bottom params of the tileRect} param0
 * @param { active page index } pageIndex
 * @param {*} stores
 * @param {current zoom level} zoomLevel
 * @param {*} options
 * Converts the tile rect coordinates in view space to be mapped to pdf coordinates
 * @returns
 */

export const transformRectViewToPDF = ({ l, r, t, b }, pageIndex, stores, zoomLevel, options) => {
  const leftTop = transformViewToPDF(l, t, pageIndex, stores, zoomLevel, options);
  const rightBottom = transformViewToPDF(r, b, pageIndex, stores, zoomLevel, options);
  const left = Math.floor(Math.min(leftTop.x, rightBottom.x));
  const bottom = Math.floor(Math.min(leftTop.y, rightBottom.y));
  const top = Math.ceil(Math.max(leftTop.y, rightBottom.y));
  const right = Math.ceil(Math.max(leftTop.x, rightBottom.x));
  return {
    left,
    right,
    top,
    bottom
  };
};

/**
     * The dimensions of the mediaBox w.r.t cropBox in the tileRect are used for rendition.
       mediaBox = [left, bottom, right, top] 
          visual:
            (left, top)   .----. (right, top)
                          |    |
            (left, bottom).----. (right, bottom)
     * 
     * This function returns the tileRect as such so as to fetch the page according to the mediaBox.
    **/

export const getTileRectAccordingToMediaBox = mediaBox => {
  /* TODO: Make Proper documentation of this function and code cleaning
    JIRA Ticket: https://jira.corp.adobe.com/browse/ADCS-27079
  */
  const [mediaBoxLeft, mediaBoxBottom, mediaBoxRight, mediaBoxTop] = mediaBox;
  return [mediaBoxLeft, mediaBoxRight, mediaBoxBottom, mediaBoxTop];
};

export const getMaxZoomLevelForPageArea = (pageHeight, pageWidth) => {
  const pageArea = pageHeight * pageWidth;
  const zoom = Math.sqrt(constants.MAX_IMAGE_SIZE / ((pageArea * constants.BITS_PER_PIXEL) / 8));
  const maxZoom = parseFloat(Math.min(zoom, constants.DEFAULT_MAX_ZOOM).toFixed(2));
  return maxZoom;
};

export const getProgressiveCanvasId = (pageIndex, progressiveRenderingWorkflow = null) => {
  if (!isNaN(pageIndex)) {
    const { PROGRESSIVE_RENDERING_WORKFLOWS } = constants;
    switch (progressiveRenderingWorkflow) {
      case PROGRESSIVE_RENDERING_WORKFLOWS.PAGE_RENDERING:
        return `progressive_page_${pageIndex + 1}`;
      case PROGRESSIVE_RENDERING_WORKFLOWS.EDIT_TYPING:
        return `progressive_edit_canvas_${pageIndex + 1}`;

      default:
        return "";
    }
  }
  return "";
};

export const getProgressiveCanvasDetails = (pageIndex, progressiveRenderingWorkflow) => {
  if (!isNaN(pageIndex)) {
    const canvasId = getProgressiveCanvasId(pageIndex, progressiveRenderingWorkflow);
    const canvas = document.getElementById(canvasId) || null;
    return { canvas, canvasId };
  }
  return {};
};

export const getDynamicCanvasContainerId = pageIndex => `container_dynamic_canvas_page_${pageIndex + 1}`;

export const getPlaceholderCanvasId = pageIndex => `progressive_canvas_placeholder_${pageIndex + 1}`;

export const createDynamicCanvas = (pageIndex, workflow) => {
  const canvasId = getProgressiveCanvasId(pageIndex, workflow);
  const dynamicCanvas = document.createElement("canvas");
  dynamicCanvas.setAttribute("id", canvasId);

  const divContainer = document.createElement("div");
  divContainer.setAttribute("id", getDynamicCanvasContainerId(pageIndex));
  divContainer.style.display = "none";

  divContainer.appendChild(dynamicCanvas);
  document.body.appendChild(divContainer);
};

export const checkIfProgressiveRenderingPossible = (
  { stores, index, itemType, priority, progressiveRenderingWorkflow },
  shouldCreateDynamicCanvas = false
) => {
  const { LOADED, UNLOADED } = loadStatus;
  const progressivelyRenderedPageStatus = getProgressivelyRenderedPageStatus(stores, index);
  const isPageProgressivelyRenderedOnce =
    progressivelyRenderedPageStatus === LOADED || progressivelyRenderedPageStatus === UNLOADED;

  const { PROGRESSIVE_RENDERING_WORKFLOWS } = constants;

  if (getUseProgressiveRendering(stores) && itemType === "page") {
    const { canvas } = getProgressiveCanvasDetails(index, progressiveRenderingWorkflow);
    if (progressiveRenderingWorkflow === PROGRESSIVE_RENDERING_WORKFLOWS.PAGE_RENDERING) {
      if (priority === constants.PAGE_PRIORITY && !isPageProgressivelyRenderedOnce && isPageBlank(stores, index)) {
        // If page canvas is already present, return true.
        // If canvas is not present, and flag is true, create new canvas and return true
        if (canvas) {
          return true;
        } else if (shouldCreateDynamicCanvas) {
          createDynamicCanvas(index, progressiveRenderingWorkflow);
          return true;
        }
      }
    } else if (Object.values(PROGRESSIVE_RENDERING_WORKFLOWS).includes(progressiveRenderingWorkflow) && canvas) {
      // Generic check for workflows other than page rendering, check if we support it in constants and
      // have the canvas related to that workflow. Inorder to support more workflows, just add key to
      // PROGRESSIVE_RENDERING_WORKFLOWS and do corrresponding handling in getProgressiveCanvasDetails
      return true;
    }
  }

  return false;
};

export const getPagesViewModeData = stores => {
  const docViewMode = getViewMode(stores);
  const viewMode = Object.keys(docViewModes).find(viewMode => docViewMode === docViewModes[viewMode]);

  const viewModeData = {
    viewMode,
    isSinglePageMode: isSinglePageMode(stores),
    isPageByPageViewMode: isPageByPageViewMode(stores),
    isContinuousViewMode: isContinuousViewMode(stores),
    isTwoColumnViewMode: isTwoColumnViewMode(stores),
    isCoverPageMode: isCoverPageMode(stores),
    isViewWithScrolling: isViewWithScrolling(stores),
    isFullScreenMode: isFullScreenMode(stores),
    isFitOneFullPageMode: isFitOneFullPageMode(stores),
    isFullScreenOrFitOnePageMode: isFullScreenOrFitOnePageMode(stores)
  };

  return viewModeData;
};

export const getTotalBottomSpace = stores => {
  const documentbottomPadding = getDocumentBottomSpace(stores) || 0;
  const bottomPanelHeight = getBottomPanelHeight(stores) || 0;
  return documentbottomPadding + bottomPanelHeight;
};
